import { useGet } from 'hooks/use-get';
import React, { useContext } from 'react';

import { getFeeders } from '../api/get-feeders';
import { useAccountState } from 'contexts/account/account-context';
import { Feeder } from 'views/self-service/features/feeders/types/feeders.type';
import { getBrands } from 'views/self-service/features/feeders/api/get-brands';
import { FeederBrand } from 'views/self-service/features/feeders/types/feeder-brand.type';

type State = {
  isLoading: boolean;
  feeders: Feeder[] | undefined;
  feederBrands: FeederBrand[] | undefined;
};

const FeedersContext = React.createContext<State | undefined>(undefined);

type FeedersProviderProps = {
  children: React.ReactNode;
};

const FeedersProvider: React.FC<FeedersProviderProps> = ({ children }) => {
  const { account } = useAccountState();

  const { data: feeders, isLoading } = useGet(['getFeeders'], () => getFeeders(String(account.company!.companyId)), {
    enabled: !!account.company!.companyId,
  });

  const { data: feederBrands } = useGet(['getBrands'], () => getBrands(account.company!.companyId), {
    enabled: !!account.company!.companyId,
    select: (data) =>
      data.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {
          numeric: true,
        })
      ),
  });

  return <FeedersContext.Provider value={{ feeders, isLoading, feederBrands }}>{children}</FeedersContext.Provider>;
};

function useFeedersState() {
  const context = useContext(FeedersContext);
  if (context === undefined) {
    throw new Error('useFeedersState must be used within a Feeders provider');
  }
  return context;
}

export { FeedersProvider, useFeedersState };
